import { useCallback, useEffect, useState } from 'react'

export type ThemeMode = 'dark' | 'light'

const STORAGE_KEY = 'legion-docs:theme'

function readStored(): ThemeMode | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw === 'dark' || raw === 'light' ? raw : null
  } catch {
    // Private windows and "block site data" both throw on access rather than
    // returning null, and neither is a reason to fail to render.
    return null
  }
}

function systemMode(): ThemeMode {
  return window.matchMedia?.('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark'
}

/**
 * The site's colour scheme: an explicit choice if one is stored, otherwise
 * whatever the OS says. Following the system is the *absence* of a stored
 * value, so a reader who never touched the toggle keeps tracking their OS
 * instead of being frozen at whatever it happened to be on first visit.
 */
export function useThemeMode() {
  const [stored, setStored] = useState<ThemeMode | null>(() =>
    typeof window === 'undefined' ? null : readStored()
  )
  const [system, setSystem] = useState<ThemeMode>(() =>
    typeof window === 'undefined' ? 'dark' : systemMode()
  )

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: light)')
    const onChange = () => setSystem(mq.matches ? 'light' : 'dark')
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const mode: ThemeMode = stored ?? system

  const setMode = useCallback((next: ThemeMode) => {
    setStored(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Storage being unavailable must not stop the toggle from working for
      // the rest of the session.
    }
  }, [])

  const toggle = useCallback(() => {
    setMode(mode === 'dark' ? 'light' : 'dark')
  }, [mode, setMode])

  return { mode, setMode, toggle }
}
